import { API_BASE_URL, MARKET_API_URL } from "../config";
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const StrategyBuilder = () => {
  const [symbol] = useState("NIFTY");
  const [spotPrice, setSpotPrice] = useState(24500);
  const [legs, setLegs] = useState([
    { strike: 24500, option_type: "CE", action: "BUY", premium: 150, qty: 25 },
    { strike: 24500, option_type: "PE", action: "BUY", premium: 140, qty: 25 }
  ]);
  const [payoff, setPayoff] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [calcLoading, setCalcLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [execMessage, setExecMessage] = useState("");

  const fetchPayoff = useCallback(async () => {
    setCalcLoading(true);
    try {
      const res = await axios.post(`${MARKET_API_URL}/api/smartapi/payoff`, {
        spot_price: Number(spotPrice),
        legs: legs
      });
      setPayoff(res.data);
    } catch (err) {
      console.error("Error calculating payoff", err);
    } finally {
      setCalcLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spotPrice, legs]);

  useEffect(() => {
    fetchPayoff();
  }, [fetchPayoff]);

  const loadPreset = (presetName) => {
    const spot = Number(spotPrice);
    if (presetName === "straddle") {
      setLegs([
        { strike: spot, option_type: "CE", action: "BUY", premium: 150, qty: 25 },
        { strike: spot, option_type: "PE", action: "BUY", premium: 140, qty: 25 }
      ]);
    } else if (presetName === "strangle") {
      setLegs([
        { strike: spot + 200, option_type: "CE", action: "BUY", premium: 80, qty: 25 },
        { strike: spot - 200, option_type: "PE", action: "BUY", premium: 75, qty: 25 }
      ]);
    } else if (presetName === "bull_call") {
      setLegs([
        { strike: spot, option_type: "CE", action: "BUY", premium: 150, qty: 25 },
        { strike: spot + 300, option_type: "CE", action: "SELL", premium: 60, qty: 25 }
      ]);
    } else if (presetName === "iron_condor") {
      setLegs([
        { strike: spot - 400, option_type: "PE", action: "BUY", premium: 20, qty: 25 },
        { strike: spot - 200, option_type: "PE", action: "SELL", premium: 75, qty: 25 },
        { strike: spot + 200, option_type: "CE", action: "SELL", premium: 80, qty: 25 },
        { strike: spot + 400, option_type: "CE", action: "BUY", premium: 25, qty: 25 }
      ]);
    }
  };

  const handleExecuteStrategy = async () => {
    setExecuting(true);
    setExecMessage("");
    try {
      for (const leg of legs) {
        const symbolStr = `${symbol}${leg.strike}${leg.option_type}`;
        await axios.post(`${API_BASE_URL}/api/client/newOrder`, {
          name: symbolStr,
          qty: leg.qty,
          price: leg.premium,
          mode: leg.action,
          productType: "MIS"
        });
      }
      setExecMessage(`Multi-leg strategy (${legs.length} legs) executed successfully in virtual OMS!`);
    } catch (err) {
      setExecMessage("Error executing strategy legs.");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div>
          <h2 style={{ margin: 0, color: "#333" }}>Multi-Leg Strategy Builder & Payoff Matrix</h2>
          <p style={{ margin: "4px 0 0", color: "#666", fontSize: "14px" }}>
            Design options spreads, calculate breakevens, and execute virtual multi-leg strategies
          </p>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={() => loadPreset("straddle")} style={presetBtnStyle}>Long Straddle</button>
          <button onClick={() => loadPreset("strangle")} style={presetBtnStyle}>Long Strangle</button>
          <button onClick={() => loadPreset("bull_call")} style={presetBtnStyle}>Bull Call Spread</button>
          <button onClick={() => loadPreset("iron_condor")} style={presetBtnStyle}>Iron Condor</button>
        </div>
      </div>

      {execMessage && (
        <div style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", padding: "10px 16px", borderRadius: "6px", marginBottom: "16px", fontWeight: "bold" }}>
          {execMessage}
        </div>
      )}

      {/* Payoff Stats Banner */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "24px" }}>
        <div style={cardStyle}>
          <div style={cardLabelStyle}>Max Profit</div>
          <div style={{ fontSize: "22px", fontWeight: "bold", color: "#2e7d32", marginTop: "4px" }}>
            {payoff?.max_profit !== undefined ? (typeof payoff.max_profit === 'number' ? `₹${payoff.max_profit.toLocaleString()}` : payoff.max_profit) : "-"}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={cardLabelStyle}>Max Loss</div>
          <div style={{ fontSize: "22px", fontWeight: "bold", color: "#c62828", marginTop: "4px" }}>
            {payoff?.max_loss !== undefined ? (typeof payoff.max_loss === 'number' ? `₹${payoff.max_loss.toLocaleString()}` : payoff.max_loss) : "-"}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={cardLabelStyle}>Breakeven Points</div>
          <div style={{ fontSize: "18px", fontWeight: "bold", color: "#1565c0", marginTop: "4px" }}>
            {payoff?.breakevens?.length > 0 ? payoff.breakevens.map(b => `₹${b}`).join(" & ") : "None"}
          </div>
        </div>
      </div>

      {/* Leg Config & Payoff Table */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
        <div style={{ backgroundColor: "#fff", padding: "20px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
          <h3 style={{ marginTop: 0, fontSize: "16px", color: "#333" }}>Strategy Legs Configuration</h3>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ fontSize: "13px", fontWeight: "bold" }}>Spot Price (₹): </label>
            <input
              type="number"
              value={spotPrice}
              onChange={(e) => setSpotPrice(e.target.value)}
              style={{ padding: "6px", borderRadius: "4px", border: "1px solid #ccc", marginLeft: "10px" }}
            />
          </div>

          <table style={{ width: "100%", fontSize: "13px", marginBottom: "16px" }}>
            <thead>
              <tr style={{ backgroundColor: "#f5f5f5" }}>
                <th>Strike</th>
                <th>Type</th>
                <th>Action</th>
                <th>Premium</th>
                <th>Qty</th>
              </tr>
            </thead>
            <tbody>
              {legs.map((leg, idx) => (
                <tr key={idx}>
                  <td>
                    <input
                      type="number"
                      value={leg.strike}
                      onChange={(e) => {
                        const newLegs = [...legs];
                        newLegs[idx].strike = Number(e.target.value);
                        setLegs(newLegs);
                      }}
                      style={{ width: "80px", padding: "4px" }}
                    />
                  </td>
                  <td>
                    <select
                      value={leg.option_type}
                      onChange={(e) => {
                        const newLegs = [...legs];
                        newLegs[idx].option_type = e.target.value;
                        setLegs(newLegs);
                      }}
                    >
                      <option value="CE">CE</option>
                      <option value="PE">PE</option>
                    </select>
                  </td>
                  <td>
                    <select
                      value={leg.action}
                      onChange={(e) => {
                        const newLegs = [...legs];
                        newLegs[idx].action = e.target.value;
                        setLegs(newLegs);
                      }}
                    >
                      <option value="BUY">BUY</option>
                      <option value="SELL">SELL</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      value={leg.premium}
                      onChange={(e) => {
                        const newLegs = [...legs];
                        newLegs[idx].premium = Number(e.target.value);
                        setLegs(newLegs);
                      }}
                      style={{ width: "70px", padding: "4px" }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={leg.qty}
                      onChange={(e) => {
                        const newLegs = [...legs];
                        newLegs[idx].qty = Number(e.target.value);
                        setLegs(newLegs);
                      }}
                      style={{ width: "50px", padding: "4px" }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button onClick={handleExecuteStrategy} disabled={executing} style={execBtnStyle}>
            {executing ? "Executing Legs..." : "⚡ Execute Multi-Leg Strategy"}
          </button>
        </div>

        {/* Payoff Simulation Table */}
        <div style={{ backgroundColor: "#fff", padding: "20px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", maxHeight: "400px", overflowY: "auto" }}>
          <h3 style={{ marginTop: 0, fontSize: "16px", color: "#333" }}>Payoff Simulation Matrix</h3>
          <table style={{ width: "100%", fontSize: "13px" }}>
            <thead>
              <tr style={{ backgroundColor: "#f5f5f5" }}>
                <th style={{ padding: "8px" }}>Underlying Spot</th>
                <th style={{ padding: "8px" }}>Strategy Net P&L (₹)</th>
              </tr>
            </thead>
            <tbody>
              {(payoff?.payoff_points || []).map((p, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: "6px" }}>₹{p.underlying_price}</td>
                  <td style={{ padding: "6px", fontWeight: "bold", color: p.pnl >= 0 ? "#2e7d32" : "#c62828" }}>
                    ₹{p.pnl}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const cardStyle = {
  backgroundColor: "#fff",
  padding: "16px",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
};

const cardLabelStyle = {
  fontSize: "12px",
  color: "#666",
  textTransform: "uppercase",
};

const presetBtnStyle = {
  padding: "8px 14px",
  backgroundColor: "#f5f5f5",
  border: "1px solid #ccc",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "12px",
  fontWeight: "bold",
};

const execBtnStyle = {
  width: "100%",
  padding: "12px",
  backgroundColor: "#4caf50",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  fontWeight: "bold",
  fontSize: "14px",
  cursor: "pointer",
};

export default StrategyBuilder;
